import AbstractModule from './AbstractModule';

/**
 * @typedef {string} GroupName
 */

/**
 * @typedef {string} RotationName
 */

/**
 * @typedef {Object} Partner
 *
 * @property {string} name  - Name of the partner.
 * @property {string} url   - URL to the partner's website.
 * @property {string} image - File path to the partner's logo.
 */

/**
 * @typedef {Object} Group
 *
 * @property {GroupName}          grouping   - Name of the group.
 * @property {Partner[] & Cycler} partners   - Partners of the group.
 * @property {Element}            $container - Container element of the group.
 * @property {Element[] & Cycler} $slots     - Slot elements for partners.
 */

/**
 * @typedef {Group[] & Cycler & Rotation} Groups
 */

/**
 * @typedef {Object} Rotation
 *
 * @property {number} intervalID - The timeoutID.
 */

/**
 * @property {number} index - Current iteration of the loop.
 */
class Cycler extends Array {
    constructor() {
        super();

        this.index = 0;
    }

    /**
     * Return the current element of the loop.
     *
     * @return {*} The current value.
     */
    current() {
        return this[this.index];
    }

    /**
     * Move forward to next iteration of the loop.
     *
     * @return {*} The new current value.
     */
    next() {
        this.index++;

        if (this.index >= this.length) {
            this.index = 0;
        }

        return this.current();
    }
}

/**
 * Fix instances of Cycler.
 *
 * This function can be removed when classes behave as expected.
 * Older bundlers fake classes using objects, which causes bad inheritance.
 *
 * @param  {Array} arr - The Array object to patch.
 * @throws {TypeError} If the argument is not an Array.
 */
function patchCycler(arr) {
    if (!Array.isArray(arr)) {
        throw new TypeError(
            'patchCycler expects argument to be an Array'
        );
    }

    if (!(arr instanceof Cycler)) {
        arr.index   = 0;
        arr.current = Cycler.prototype.current;
        arr.next    = Cycler.prototype.next;
    }
}

/**
 * Logo Partners Iterator
 */
export default class extends AbstractModule {
    constructor(options) {
        super(options);

        this.dataAttr = 'data-logo-partners';

        this.DELAY = this.$el.attr(`${this.dataAttr}-delay`) || 1000;
        this.SPEED = this.$el.attr(`${this.dataAttr}-speed`) || 300;

        /** @type {Object<GroupName, Partner[]>} */
        this.partners = {};

        /** @type {Object<RotationName, Groups>} */
        this.rotations = {};

        this.init();
    }

    init() {
        this.partners = this.fetchPartners();

        this.el.querySelectorAll(`[${this.dataAttr}-group]`).forEach(($container) => {
            const rotation = $container.getAttribute(`${this.dataAttr}-rotation`);

            if (!rotation) {
                return;
            }

            if (!this.rotations[rotation]) {
                this.rotations[rotation] = new Cycler();
                patchCycler(this.rotations[rotation]);
            }

            const groupings = $container.getAttribute(`${this.dataAttr}-group`)
                .split(/(\s*,+\s*|\s+)/)
                .map((grouping) => grouping.trim())
                .filter((grouping) => grouping);

            groupings.forEach((grouping) => {
                if (!grouping) {
                    return;
                }

                let partners = this.partners[grouping];
                if (!Array.isArray(partners) || partners.length === 0) {
                    return;
                }

                partners = Cycler.from(partners);
                patchCycler(partners);

                let $slots = $container.querySelectorAll(`[${this.dataAttr}-slot="${grouping}"]`);
                if (!$slots.length) {
                    return;
                }

                $slots = Cycler.from($slots);
                patchCycler($slots);

                if (partners.length <= $slots.length) {
                    return;
                }

                partners.index = $slots.length;

                this.rotations[rotation].push({
                    grouping,
                    partners,
                    $container,
                    $slots,
                });
            });
        });

        // console.log('[LogoPartners]', 'Rotations:', this.rotations);

        for (const rotation in this.rotations) {
            this.rotateSlots(this.rotations[rotation], rotation);
        }
    }

    fetchPartners() {
        const $script = this.el.querySelector(`script[${this.dataAttr}-collection]`);

        if ($script) {
            const jsonText = $script.innerHTML;

            if (jsonText) {
                try {
                    const jsonData = JSON.parse(jsonText)
                    if (jsonData) {
                        // console.log('[LogoPartners]', ' Partners:', jsonData);
                        return jsonData;
                    }
                } catch (err) {
                    console.error('[LogoPartners]', 'Failed to fetch partners:', err);
                }
            }
        }

        return null;
    }

    /**
     * @param {Groups} groups
     * @param {string} rotation
     */
    rotateSlots(groups, rotation) {
        groups.intervalID = setInterval(() => {
            try {
                const group = groups.current();

                if (!group) {
                    throw new Error('Group not found');
                }

                const partner = group.partners.current();

                if (!partner) {
                    throw new Error('Partner not found');
                }

                const $slot = group.$slots.current();

                if (!$slot) {
                    throw new Error('Element \'[data-logo-partners-slot]\' not found');
                }

                // console.log(rotation, '--', 'Group:', groups.index, 'Slot:', group.$slots.index, 'Partner:', group.partners.index);

                groups.next();
                group.partners.next();
                group.$slots.next();

                const $link = $slot.classList.contains('js-partner-link')
                    ? $slot
                    : $slot.querySelector('.js-partner-link');

                if (!$link) {
                    throw new Error('Element \'.js-partner-link\' not found');
                }

                const $image = $slot.querySelector('.js-partner-logo');

                if (!$image) {
                    throw new Error('Element \'.js-partner-logo\' not found');
                }

                $image.classList.add('is-hide');

                setTimeout(() => {
                    try {
                        $link.setAttribute('href', partner.url);
                        $image.setAttribute('alt', partner.name);
                        $image.setAttribute('src', partner.image);

                        $image.classList.remove('is-hide');
                    } catch (err) {
                        console.error('[LogoPartners]', 'Failed to rotate slot:', err);
                    }
                }, this.SPEED);
            } catch (err) {
                console.error('[LogoPartners]', 'Failed to rotate slots:', err);
                clearInterval(groups.intervalID);
            }
        }, this.DELAY + this.SPEED);
    }

    destroy() {
        for (const rotation in this.rotations) {
            const groups = this.rotations[rotation];

            if (groups.intervalID) {
                clearInterval(groups.intervalID);
            }
        }
    }
}
